import { Component, computed, inject, input } from '@angular/core';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts';
import * as graphLightTheme from 'src/app/shared/graph/ok-cvi-lightmode.json';
import { ECBasicOption } from 'echarts/types/dist/shared';
import { DkCurrencyPipe } from 'src/app/shared/pipes/dk-currency';
import { PriceChartDataResponse } from '../../dynamic-price-chart.interface';

@Component({
  selector: 'adm-dynamic-price-chart',
  templateUrl: './dynamic-price-chart.component.html',
  styleUrls: ['./dynamic-price-chart.component.scss'],
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts }), DkCurrencyPipe],
})
export class DynamicPriceChartComponent {
  private dkCurrencyPipe = inject(DkCurrencyPipe);

  data = input.required<PriceChartDataResponse>();

  public graphLightTheme = graphLightTheme as unknown as JSON;
  public chartOptions = computed<ECBasicOption>(() => this.buildChartOptions(this.data()));
  public formattedLowLimit = computed(() => this.dkCurrencyPipe.transform(this.data().lowLimitSpotPriceInKrExcVat));
  public formattedHighLimit = computed(() => this.dkCurrencyPipe.transform(this.data().highLimitSpotPriceInKrExcVat));

  private buildChartOptions(data: PriceChartDataResponse): ECBasicOption {
    const forecast = data.forecast;
    const timeLabels = forecast.map(f => this.formatTime(new Date(f.fromTime)));
    const intervalLabels = forecast.map(f => {
      return `${this.formatTime(new Date(f.fromTime))} - ${this.formatTime(new Date(f.toTime))}`;
    });

    const formatCurrency = (value: number) => this.dkCurrencyPipe.transform(value) ?? '';

    return {
      tooltip: [
        {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          valueFormatter: (value: number) => formatCurrency(value),
          formatter: (params: { seriesName: string; value: number; marker: string; dataIndex: number }[]) => {
            const index = params[0].dataIndex;
            let result = `${intervalLabels[index]}<br/><br/>`;
            for (const p of [...params].reverse()) {
              result += `${p.marker} ${p.seriesName}: ${formatCurrency(p.value)}<br/>`;
            }
            result += `<br/><b>Forbrugerpris: ${formatCurrency(forecast[index].totalPriceInclVat)}</b>`;
            return result;
          },
        },
        {
          trigger: 'item',
        },
      ],
      legend: {
        data: ['Strømsats', 'OK-tillæg', 'Kundetillæg', 'Moms'],
      },
      xAxis: {
        type: 'category',
        data: timeLabels,
      },
      yAxis: {
        type: 'value',
        name: 'Kr',
        axisLabel: {
          formatter: (value: number) => formatCurrency(value),
        },
      },
      series: [
        {
          name: 'Strømsats',
          type: 'bar',
          stack: 'price',
          data: forecast.map(f => f.clampedPriceInKrExcVat),
          markLine: {
            symbol: 'none',
            label: { show: false },
            tooltip: {
              trigger: 'item',
              formatter: (params: { name: string; value: number }) => `${params.name}: ${formatCurrency(params.value)}`,
            },
            data: [
              {
                name: 'Strømsats bund',
                yAxis: data.lowLimitSpotPriceInKrExcVat,
                lineStyle: { type: 'dashed', color: '#000f19', width: 1 },
              },
              {
                name: 'Strømsats loft',
                yAxis: data.highLimitSpotPriceInKrExcVat,
                lineStyle: { type: 'dashed', color: '#8c0000', width: 2 },
              },
            ],
          },
        },
        { name: 'OK-tillæg', type: 'bar', stack: 'price', data: forecast.map(f => f.okSupplementInKrExcVat) },
        { name: 'Kundetillæg', type: 'bar', stack: 'price', data: forecast.map(f => f.organisationSupplementInKrExcVat) },
        { name: 'Moms', type: 'bar', stack: 'price', data: forecast.map(f => f.totalVatInKr) },
      ],
    };
  }

  private formatTime(date: Date): string {
    return date.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
  }
}
